import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../axiosInstance";
import { trackPurchase } from "../../analytics/analytics";

// The shared `api` instance already sets baseURL, withCredentials, and
// (if you kept the Bearer fallback) the Authorization header, so no
// thunk builds auth headers by hand any more.

// ─── CREATE ORDER ─────────────────────────────────────────────
export const createOrder = createAsyncThunk(
  "order/createOrder",
  async (orderData, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/order/create-order`, orderData);

      // Fire GA4 purchase event on successful order creation
      if (data?.order) {
        trackPurchase(data.order);
      }

      return data.order;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to place order",
      );
    }
  },
);

// ─── GET ALL ORDERS (ADMIN) ───────────────────────────────────
export const getAllOrders = createAsyncThunk(
  "order/getAllOrders",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/order/get-all-orders`);
      return data.orders;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch orders",
      );
    }
  },
);

// ─── GET MY ORDERS ────────────────────────────────────────────
export const getMyOrders = createAsyncThunk(
  "order/getMyOrders",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/order/my-orders`);
      return data.orders;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch your orders",
      );
    }
  },
);

// ─── GET SINGLE ORDER ─────────────────────────────────────────
export const getOrderDetails = createAsyncThunk(
  "order/getOrderDetails",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/order/get-order/${id}`);
      return data.order;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch order details",
      );
    }
  },
);

// ─── UPDATE ORDER STATUS ──────────────────────────────────────
export const updateOrderStatus = createAsyncThunk(
  "order/updateOrderStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/order/update-order-status/${id}`, {
        status,
      });
      return data.order;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update status",
      );
    }
  },
);

// ─── GET ORDER BY NUMBER ──────────────────────────────────────
export const getOrderByNo = createAsyncThunk(
  "order/getOrderByNo",
  async (orderNo, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/order/get-order-by-no/${orderNo}`);
      return data.order;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch order",
      );
    }
  },
);

// ─── DELETE ORDER ─────────────────────────────────────────────
export const deleteOrder = createAsyncThunk(
  "order/deleteOrder",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`/order/delete-order/${id}`);
      return { id, message: data.message };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete order",
      );
    }
  },
);

// ═══ RETURN THUNKS ═══════════════════════════════════════════════════════

export const getAdminReturnableOrders = createAsyncThunk(
  "order/getAdminReturnableOrders",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/order/returnable-orders`);
      return data.orders;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch returnable orders",
      );
    }
  },
);

export const processAdminReturn = createAsyncThunk(
  "order/processAdminReturn",
  async ({ orderId, returns }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/order/return-product`, {
        orderId,
        returns,
      });
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to process return",
      );
    }
  },
);

export const getAdminReturnDetails = createAsyncThunk(
  "order/getAdminReturnDetails",
  async (orderId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/order/return-details/${orderId}`);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch return details",
      );
    }
  },
);

export const getMyReturns = createAsyncThunk(
  "order/getMyReturns",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/order/my-returns`);
      return data.returns;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch returns",
      );
    }
  },
);

export const getMyReturnDetails = createAsyncThunk(
  "order/getMyReturnDetails",
  async (orderId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/order/my-returns/${orderId}`);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch return details",
      );
    }
  },
);

// ─── SLICE ────────────────────────────────────────────────────
const orderSlice = createSlice({
  name: "order",
  initialState: {
    order: null,
    orders: [],
    myOrders: [],
    returnableOrders: [],
    orderDetails: null,
    returnDetails: null,
    myReturns: [],
    myReturnDetails: null,
    loading: false,
    error: null,
    success: false,
    message: null,
  },
  reducers: {
    clearOrderState: (state) => {
      state.order = null;
      state.orders = [];
      state.myOrders = [];
      state.returnableOrders = [];
      state.orderDetails = null;
      state.returnDetails = null;
      state.myReturns = [];
      state.myReturnDetails = null;
      state.loading = false;
      state.error = null;
      state.success = false;
      state.message = null;
    },
    clearOrderError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Order
      .addCase(createOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.order = action.payload;
        state.success = true;
        state.message = "Order placed successfully";
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Get All Orders
      .addCase(getAllOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload;
      })
      .addCase(getAllOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get My Orders
      .addCase(getMyOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMyOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.myOrders = action.payload;
      })
      .addCase(getMyOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Order Details
      .addCase(getOrderDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOrderDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.orderDetails = action.payload;
      })
      .addCase(getOrderDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Order Status
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.message = "Status updated successfully";

        // Update in orders list
        const idx = state.orders.findIndex((o) => o._id === action.payload._id);
        if (idx !== -1) state.orders[idx] = action.payload;

        // Update in myOrders list
        const myIdx = state.myOrders.findIndex(
          (o) => o._id === action.payload._id,
        );
        if (myIdx !== -1) state.myOrders[myIdx] = action.payload;

        // Update current detail view
        if (state.orderDetails?._id === action.payload._id) {
          state.orderDetails = action.payload;
        }
      })

      // Get Order By No
      .addCase(getOrderByNo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOrderByNo.fulfilled, (state, action) => {
        state.loading = false;
        state.orderDetails = action.payload;
      })
      .addCase(getOrderByNo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.orderDetails = null;
      })

      // Delete Order
      .addCase(deleteOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;
        state.orders = state.orders.filter((o) => o._id !== action.payload.id);
        state.myOrders = state.myOrders.filter(
          (o) => o._id !== action.payload.id,
        );
        if (state.orderDetails?._id === action.payload.id) {
          state.orderDetails = null;
        }
      })
      .addCase(deleteOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Admin Returnable Orders
      .addCase(getAdminReturnableOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdminReturnableOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.returnableOrders = action.payload;
      })
      .addCase(getAdminReturnableOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Process Admin Return
      .addCase(processAdminReturn.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(processAdminReturn.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "Return processed successfully";
        const idx = state.returnableOrders.findIndex(
          (o) => o._id === action.payload.order._id,
        );
        if (idx !== -1) {
          state.returnableOrders[idx] = {
            ...state.returnableOrders[idx],
            ...action.payload.order,
          };
        }
      })
      .addCase(processAdminReturn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Admin Return Details
      .addCase(getAdminReturnDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAdminReturnDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.returnDetails = action.payload;
      })
      .addCase(getAdminReturnDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // My Returns
      .addCase(getMyReturns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMyReturns.fulfilled, (state, action) => {
        state.loading = false;
        state.myReturns = action.payload;
      })
      .addCase(getMyReturns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // My Return Details
      .addCase(getMyReturnDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMyReturnDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.myReturnDetails = action.payload;
      })
      .addCase(getMyReturnDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearOrderState, clearOrderError } = orderSlice.actions;
export default orderSlice.reducer;